import { useAuth } from '@/hooks/useAuth';
import { preferencesApi, type UserPreferencesPayload } from '@/lib/api/user';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

interface PreferencesContextType {
  preferences: UserPreferencesPayload;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updatePreferences: (
    updates: Partial<UserPreferencesPayload>
  ) => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(
  undefined
);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferencesPayload>({
    theme: 'system',
    date_format: 'MM/DD/YYYY',
    timezone: 'UTC',
    platform_tour_completed: false,
  });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreferences = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      setInitialized(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const prefs = await preferencesApi.get();

      setPreferences({
        theme: prefs.theme || 'system',
        date_format: prefs.date_format || 'MM/DD/YYYY',
        timezone: prefs.timezone || 'UTC',
        platform_tour_completed: prefs.platform_tour_completed ?? false,
        onboarding_status: prefs.onboarding_status ?? 'not_started',
      });

      setInitialized(true);
    } catch (e: unknown) {
      setError((e as Error)?.message ?? 'Failed to load preferences');
      console.error('Failed to load preferences:', e);
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (
    updates: Partial<UserPreferencesPayload>
  ) => {
    if (!isAuthenticated) {
      throw new Error('Must be authenticated to update preferences');
    }

    try {
      setError(null);
      const updated = await preferencesApi.update(updates);
      setPreferences({
        theme: updated.theme || 'system',
        date_format: updated.date_format || 'MM/DD/YYYY',
        timezone: updated.timezone || 'UTC',
        onboarding_status:
          updated.onboarding_status ??
          preferences.onboarding_status ??
          'not_started',
        platform_tour_completed:
          updated.platform_tour_completed ??
          preferences.platform_tour_completed ??
          false,
      });
    } catch (e: unknown) {
      const errorMsg = (e as Error)?.message ?? 'Failed to update preferences';
      setError(errorMsg);
      console.error('Failed to update preferences:', e);
      throw e;
    }
  };

  useEffect(() => {
    fetchPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        loading,
        initialized,
        error,
        refetch: fetchPreferences,
        updatePreferences,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
